import { useRef, useEffect } from 'react';
import Editor from '@toast-ui/editor';
import Viewer from '@toast-ui/editor/viewer';
import '@toast-ui/editor/dist/toastui-editor.css';
import tableMergedCell from '@toast-ui/editor-plugin-table-merged-cell';
import '@toast-ui/editor-plugin-table-merged-cell/dist/toastui-editor-plugin-table-merged-cell.css';


function ToastEditor({ initialValue = '', onChange, disable = false, disabledChange = false }) {
     const editorContainerRef = useRef(null);
     const editorInstanceRef = useRef(null);
     const onChangeRef = useRef(onChange);

     useEffect(() => {
          onChangeRef.current = onChange;
     }, [onChange]);

     useEffect(() => {
          const container = editorContainerRef.current;
          if (!container) return;

          container.innerHTML = '';

          if (disable) {
               const viewer = new Viewer({
                    el: container,
                    initialValue,
               });
               container.classList.add('custom-tui-viewer');
               editorInstanceRef.current = viewer;
          } else {
               const editor = new Editor({
                    el: container,
                    height: '400px',
                    initialEditType: 'markdown',
                    previewStyle: 'vertical',
                    initialValue,
                    language: 'en',
                    viewer: false,
                    events: {
                         change: () => {
                              onChangeRef.current?.(editor.getMarkdown());
                         },
                    },
                    toolbarItems: [
                         ['heading', 'bold', 'italic'],
                         ['hr', 'quote'],
                         ['ul', 'ol'],
                         ['table', 'link']
                    ],
                    plugins: [tableMergedCell],
               });
               container.classList.remove('custom-tui-viewer');
               editorInstanceRef.current = editor;
          }

          if (disabledChange) {
               container.classList.add('toast-editor-disabled');

               const editorMarkdownRoot = container.querySelector('.toastui-editor.md-mode');
               const editorWysiwygRoot = container.querySelector('.toastui-editor.ww-mode');

               const preventAll = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
               }

               const eventListenerKeydownAndPaste = (element, isAddEvent = true) => {
                    if (!element) return;
                    const proseMirrorElm = element.querySelector('.ProseMirror');
                    if (!proseMirrorElm) return;

                    if (isAddEvent) {
                         proseMirrorElm.setAttribute('contenteditable', 'false');
                         proseMirrorElm.addEventListener('keydown', preventAll, true);
                         proseMirrorElm.addEventListener('paste', preventAll, true);
                    } else {
                         proseMirrorElm.setAttribute('contenteditable', 'true');
                         proseMirrorElm.removeEventListener('keydown', preventAll, true);
                         proseMirrorElm.removeEventListener('paste', preventAll, true);
                    }
               }

               eventListenerKeydownAndPaste(editorMarkdownRoot);
               eventListenerKeydownAndPaste(editorWysiwygRoot);

               // Store cleanup function to remove listeners
               editorInstanceRef.current._customCleanups = () => {
                    eventListenerKeydownAndPaste(editorMarkdownRoot, false);
                    eventListenerKeydownAndPaste(editorWysiwygRoot, false);
               };

          } else {
               container.classList.remove('toast-editor-disabled');
          }

          return () => {
               const instance = editorInstanceRef.current;
               
               if (instance && typeof instance._customCleanups === 'function') {
                    instance._customCleanups();
               }

               // Prevent removeChild error by checking if editor's element is still in the DOM
               if (instance && container?.contains(instance.el)) {
                    instance.destroy();
               }
               editorInstanceRef.current = null;
          };
     }, [disable, disabledChange]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          const instance = editorInstanceRef.current;

          if (instance && !disable && typeof instance.setMarkdown === 'function') {
               if (instance.getMarkdown() !== initialValue) {
                    instance.setMarkdown(initialValue || '');
               }
          }
     }, [disable]); // eslint-disable-line react-hooks/exhaustive-deps

     return <div ref={editorContainerRef} />;
}

export default ToastEditor;
